import authService from './auth.service';
import type {
  CreateConversationPayload,
  CreateConversationResponse,
  SendMessageResponse,
  PollLatestResponse,
  ConversationDetail,
  ConversationListResponse,
  ConversationListParams,
  SupportConversation,
} from '../types/conversation.types';

const API_URL = '/api/v1';

const conversationService = {
  async createConversation(payload?: CreateConversationPayload): Promise<CreateConversationResponse> {
    const res = await authService.fetchWithAuth(`${API_URL}/support/conversations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload ?? {}),
    });
    if (!res.ok) throw new Error(`Failed to create conversation: ${res.status}`);
    return res.json();
  },

  async sendMessage(conversationId: string, content: string, files?: File[]): Promise<SendMessageResponse> {
    const formData = new FormData();
    formData.append('content', content);
    if (files) {
      for (const file of files) {
        formData.append('files', file);
      }
    }
    const res = await authService.fetchWithAuth(
      `${API_URL}/support/conversations/${conversationId}/messages`,
      { method: 'POST', body: formData },
    );
    if (res.status === 409) {
      throw new ConversationBusyError();
    }
    if (!res.ok) throw new Error(`Failed to send message: ${res.status}`);
    return res.json();
  },

  async pollLatestMessage(conversationId: string): Promise<PollLatestResponse> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/support/conversations/${conversationId}/messages/latest`,
    );
    if (!res.ok) throw new Error(`Failed to poll: ${res.status}`);
    return res.json();
  },

  async getConversation(conversationId: string): Promise<ConversationDetail> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/support/conversations/${conversationId}`,
    );
    if (!res.ok) throw new Error(`Failed to get conversation: ${res.status}`);
    return res.json();
  },

  async adminGetConversation(conversationId: string): Promise<ConversationDetail> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/support/admin/conversations/${conversationId}`,
    );
    if (!res.ok) throw new Error(`Failed to get conversation: ${res.status}`);
    return res.json();
  },

  async listConversations(params?: ConversationListParams): Promise<ConversationListResponse> {
    const q = new URLSearchParams();
    if (params?.offset != null) q.set('offset', String(params.offset));
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.status) q.set('status', params.status);
    const qs = q.toString();
    const res = await authService.fetchWithAuth(
      `${API_URL}/support/conversations${qs ? `?${qs}` : ''}`,
    );
    if (!res.ok) throw new Error(`Failed to list conversations: ${res.status}`);
    return res.json();
  },

  async closeConversation(conversationId: string): Promise<SupportConversation> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/support/conversations/${conversationId}/close`,
      { method: 'POST' },
    );
    if (!res.ok) throw new Error(`Failed to close conversation: ${res.status}`);
    return res.json();
  },
};

export class ConversationBusyError extends Error {
  constructor() {
    super('Agent is still processing previous message');
    this.name = 'ConversationBusyError';
  }
}

export default conversationService;
